import React, { useEffect, useRef } from "react";

/* ═══════════════════════════════════════════════════════════
   AMBIENT BACKGROUND — Floating Gold Dust
   Lightweight canvas particle system · GPU-friendly
   Dampens on mobile · respects reduced motion
   ═══════════════════════════════════════════════════════════ */

const AmbientBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const isMobile =
      window.matchMedia("(pointer: coarse)").matches ||
      window.innerWidth < 768;

    // On mobile or reduced motion — render a static, lightweight gradient
    if (prefersReduced || isMobile) {
      const drawStatic = () => {
        const w = (canvas.width = window.innerWidth);
        const h = (canvas.height = window.innerHeight);
        const grad = ctx.createRadialGradient(
          w * 0.5,
          h * 0.3,
          0,
          w * 0.5,
          h * 0.3,
          Math.max(w, h) * 0.7
        );
        grad.addColorStop(0, "rgba(212, 175, 55, 0.04)");
        grad.addColorStop(0.5, "rgba(10, 10, 12, 0)");
        grad.addColorStop(1, "rgba(6, 6, 7, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      };
      drawStatic();
      window.addEventListener("resize", drawStatic);
      return () => window.removeEventListener("resize", drawStatic);
    }

    // ── Desktop: floating gold dust particles ──
    const particles = [];
    const PARTICLE_COUNT = Math.min(
      80,
      Math.floor((window.innerWidth * window.innerHeight) / 18000)
    );

    class Particle {
      constructor() {
        this.reset(true);
      }
      reset(initial = false) {
        this.x = Math.random() * canvas.width;
        this.y = initial
          ? Math.random() * canvas.height
          : -10;
        this.size = Math.random() * 1.8 + 0.4;
        this.speedY = Math.random() * 0.3 + 0.08;
        this.speedX = (Math.random() - 0.5) * 0.15;
        this.opacity = Math.random() * 0.5 + 0.1;
        this.twinkleSpeed = Math.random() * 0.02 + 0.005;
        this.twinklePhase = Math.random() * Math.PI * 2;
        this.gold = Math.random() > 0.3;
      }
      update() {
        this.y -= this.speedY;
        this.x += this.speedX;
        this.twinklePhase += this.twinkleSpeed;
        if (this.y < -10) this.reset();
        if (this.x < -10) this.x = canvas.width + 10;
        if (this.x > canvas.width + 10) this.x = -10;
      }
      draw() {
        const twinkle = 0.6 + 0.4 * Math.sin(this.twinklePhase);
        const alpha = this.opacity * twinkle;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.gold
          ? `rgba(212, 175, 55, ${alpha})`
          : `rgba(200, 200, 210, ${alpha * 0.5})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(new Particle());
    }

    let rafId;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Deep obsidian vignette
      const grad = ctx.createRadialGradient(
        canvas.width * 0.5,
        canvas.height * 0.4,
        0,
        canvas.width * 0.5,
        canvas.height * 0.4,
        Math.max(canvas.width, canvas.height) * 0.8
      );
      grad.addColorStop(0, "rgba(20, 20, 26, 0.35)");
      grad.addColorStop(0.6, "rgba(10, 10, 12, 0.1)");
      grad.addColorStop(1, "rgba(6, 6, 7, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      rafId = requestAnimationFrame(tick);
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="ambient-canvas" aria-hidden="true" />;
};

export default AmbientBackground;